import { SALE_API_URL } from "../../../constants";
import { apiSlice } from "../../../services";
import { IApiRes } from "../../../types/interfaces";
import { FeatureRecommendationSettingType, OrderFlowSettingType, StoreSettingType } from "../types/interfaces";

// Request interfaces
export interface ICreateStoreReq {
    org_id: string;
    address: string;
    latitude: number;
    longitude: number;
    phone: string;
    name: string;
}

export interface IUpdateStoreSettingReq {
    store_id: string;
    address: string;
    latitude: number;
    longitude: number;
    phone: string;
    store_name: string;
}

export interface IUpdateOrderFlowSettingReq {
    store_id: string;
    order_distinguish_confused_items: boolean;
    specification_enhance_match: boolean;
    announce_product_on_add_to_cart: boolean;
}

export interface IUpdateFeatureRecommendationSettingReq {
    store_id: string;
    item_type: string[];
    knowledge_type: string[];
    use_accurate_knowledge_popup?: boolean;
    embedding_search_limit?: number;
    fulltext_search_limit?: number;
    embedding_search_min_score?: number;
    fulltext_search_min_score?: number;
    tagrecommend_limit?: number;
}

export interface IUpdateDiningOptionsReq {
    store_id: string;
    available_dining_options?: string[];
    available_payment_methods?: string[];
    chat_mode?: string;
    chat_mode_version?: string;
    use_org_corrector?: boolean;
    use_tag_recommend?: boolean;
    enable_web_login?: boolean;
    prompt?: string;
}

export interface IGetStoreSettingReq {
    store_id: string;
}

export interface IGetAccountStoresReq {
    account_id: string;
}

export interface IDeleteStoreReq {
    store_id: string;
}

export interface IGetLinePayConfigReq {
    store_id: string;
}

export interface IUpdateLinePayConfigReq {
    store_id: string;
    enabled: boolean;
    environment: string;
    channel_id: string;
    channel_secret?: string;
    webhook_url?: string;
    merchant_note?: string;
}

export interface ITestLinePayConnectionReq {
    store_id: string;
}

// Response interfaces
export interface IGetStoreSettingRes {
    data: StoreSettingType;
}

export interface IGetOrderFlowSettingRes {
    data: OrderFlowSettingType;
}

export interface IGetFeatureRecommendationSettingRes {
    data: FeatureRecommendationSettingType;
}

export interface IGetDiningOptionsRes extends IApiRes {
    data: {
        chat_mode: string;
        chat_mode_version?: string;
        prompt: string;
        use_org_corrector: boolean;
        use_tag_recommend: boolean;
        enable_web_login: boolean;
        enable_dining_option: boolean;
        available_dining_options: string[];
        available_payment_methods?: string[];
    };
}

export interface ICreateStoreRes {
    data: {
        id: string;
        store_name: string;
    };
}

export interface IGetAccountStoresRes {
    data: {
        total_count: number;
        result: {
            id: string;
            store_name: string;
            address: string;
            phone: string;
            latitude: number;
            longitude: number;
            org_id: string;
            branch_mode: string;
            created_at: string;
            updated_at: string;
            deleted_at: null | string;
        }[];
    };
}

export interface IAccountInfoRes extends IApiRes {
    data: {
        id: string;
        org_id: string;
        email: string;
        username: string;
        display_name: string;
        roles: [
            {
                role_code: string;
                role_name: string;
                description: string;
            }
        ];
    };
}

export interface IGetStoreInfoRes extends IApiRes {
    data: {
        result: [
            {
                address: string;
                chat_mode: string;
                created_at: string;
                deleted_at: string;
                id: string;
                latitude: number;
                longitude: number;
                org_id: string;
                phone: string;
                store_name: string;
                updated_at: string;
            }
        ];
        total_count: number;
    };
}
export interface IGetStoreInfoReq {
    org_id: string;
    page: number;
    limit: number;
}

export interface IGetLinePayConfigRes extends IApiRes {
    data: {
        store_id: string;
        enabled: boolean;
        environment: string;
        channel_id: string;
        has_channel_secret: boolean;
        webhook_url: string | null;
        merchant_note: string;
        last_tested_at: string | null;
        test_status: string;
    };
}

export const storeApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // Get store info
        getStoreInfo: builder.query<IGetStoreInfoRes, IGetStoreInfoReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/orgs/${params.org_id}/stores`,
                method: "GET",
                params,
            }),
            providesTags: ["SalesItems"],
        }),

        // Get account info
        getAccountInfo: builder.query<IAccountInfoRes, void>({
            query: () => ({
                url: `${SALE_API_URL}/account/me`,
                method: "GET",
            }),
        }),

        // Get account stores
        getAccountStores: builder.query<IGetAccountStoresRes, IGetAccountStoresReq>({
            query: (params) => ({
                url: `/account/${params.account_id}/stores`,
                method: "GET",
            }),
            providesTags: ["SalesItems"],
        }),

        // Create store
        createStore: builder.mutation<ICreateStoreRes, ICreateStoreReq>({
            query: (params) => {
                const { org_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/orgs/${org_id}/stores`,
                    method: "POST",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Get store setting
        getStoreSetting: builder.query<IGetStoreSettingRes, IGetStoreSettingReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}/setting`,
                method: "GET",
            }),
            providesTags: ["SalesItems"],
        }),

        // Get order flow setting
        getOrderFlowSetting: builder.query<IGetOrderFlowSettingRes, IGetStoreSettingReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}/order_flow_setting`,
                method: "GET",
            }),
            providesTags: ["SalesItems"],
        }),

        // Get feature recommendation setting
        getFeatureRecommendationSetting: builder.query<IGetFeatureRecommendationSettingRes, IGetStoreSettingReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}/feature_recommendation_setting`,
                method: "GET",
            }),
            providesTags: ["SalesItems"],
        }),

        // Update store setting
        updateStoreSetting: builder.mutation<IApiRes, IUpdateStoreSettingReq>({
            query: (params) => {
                const { store_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/setting`,
                    method: "PATCH",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Update order flow setting
        updateOrderFlowSetting: builder.mutation<IApiRes, IUpdateOrderFlowSettingReq>({
            query: (params) => {
                const { store_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/order_flow_setting`,
                    method: "PATCH",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Update feature recommendation setting
        updateFeatureRecommendationSetting: builder.mutation<IApiRes, IUpdateFeatureRecommendationSettingReq>({
            query: (params) => {
                const { store_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/feature_recommendation_setting`,
                    method: "PATCH",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Get dining options setting
        getDiningOptions: builder.query<IGetDiningOptionsRes, IGetStoreSettingReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}/setting`,
                method: "GET",
            }),
            providesTags: ["SalesItems"],
        }),

        // Update dining options setting
        updateDiningOptions: builder.mutation<IApiRes, IUpdateDiningOptionsReq>({
            query: (params) => {
                const { store_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/setting`,
                    method: "PATCH",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Delete store
        deleteStore: builder.mutation<IApiRes, IDeleteStoreReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["SalesItems"],
        }),

        // Get LINE Pay config
        getLinePayConfig: builder.query<IGetLinePayConfigRes, IGetLinePayConfigReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}/linepay/config`,
                method: "GET",
            }),
            providesTags: ["SalesItems"],
        }),

        // Update LINE Pay config
        updateLinePayConfig: builder.mutation<IApiRes, IUpdateLinePayConfigReq>({
            query: (params) => {
                const { store_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/linepay/config`,
                    method: "PUT",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Test LINE Pay connection
        testLinePayConnection: builder.mutation<IApiRes, ITestLinePayConnectionReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/stores/${params.store_id}/linepay/test`,
                method: "POST",
            }),
            invalidatesTags: ["SalesItems"],
        }),
    }),
});

export const {
    useGetAccountInfoQuery,
    useGetAccountStoresQuery,
    useCreateStoreMutation,
    useGetStoreSettingQuery,
    useGetOrderFlowSettingQuery,
    useGetFeatureRecommendationSettingQuery,
    useUpdateStoreSettingMutation,
    useUpdateOrderFlowSettingMutation,
    useUpdateFeatureRecommendationSettingMutation,
    useGetStoreInfoQuery,
    useGetDiningOptionsQuery,
    useUpdateDiningOptionsMutation,
    useDeleteStoreMutation,
    useGetLinePayConfigQuery,
    useUpdateLinePayConfigMutation,
    useTestLinePayConnectionMutation,
} = storeApiSlice;
